import { useEffect } from 'react';
import { useRouter } from 'next/router';
import Head from 'next/head';
import { useSubscriptionProration } from '../../hooks/useSubscriptionProration';
import { useSubscriptionUpdate } from '../../hooks/useSubscriptionUpdate';
import { SubscriptionForm } from '../../components/SubscriptionForm';
import { useAuth0User } from '@hooks/useAuth0User';

import { useAccessToken } from '@hooks/useAccessToken';
import { WEBSITE_NAME } from '@lib/constants';
import AnimateFootball from '@components/animate-football';

const UpdateStripeSubscriptionPage: React.FC = () => {
  const { user, isLoading } = useAuth0User();
  const { accessToken } = useAccessToken();
  const router = useRouter();
  const { planId } = router.query;

  const {
    subscriptionState,
    pending: prorationPending,
    error: prorationError,
    fetchProrations,
    resetState
  } = useSubscriptionProration(user, accessToken);

  const {
    updateSubscription,
    pending: updatePending,
    error: updateError
  } = useSubscriptionUpdate(accessToken, router);

  useEffect(() => {
    if (!user) {
      resetState();
      return;
    }

    if (accessToken && planId) {
      fetchProrations(planId);
    }
  }, [user, accessToken, planId, fetchProrations, resetState]);

  // Handlers
  const handleSubmit = async () => {
    if (!accessToken) {
      console.error('Access token not available');
      return;
    }

    await updateSubscription({
      tier: subscriptionState?.newSubscriptionTier,
      term: subscriptionState?.newSubscriptionTerm || ''
    });
  };

  if (isLoading || !accessToken) {
    return <AnimateFootball />;
  }

  const pending = prorationPending || updatePending;
  const error = prorationError || updateError;
  const ready = !pending && subscriptionState?.newSubscriptionPrice;

  return (
    <div>
      <Head>
        <title>{`Change Plan | ${WEBSITE_NAME}`}</title>
      </Head>

      <main>
        <div className="flex flex-col justify-center py-8 md:py-24 bg-gray-50 sm:px-6 lg:px-8">
          <div className="sm:mx-auto sm:w-full sm:max-w-xl">
            <h2 className="text-3xl font-extrabold text-center text-gray-900">
              Change your plan
            </h2>
          </div>

          <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-xl">
            <div className="px-4 py-8 bg-white shadow sm:rounded-lg sm:px-10">
              {!ready ? (
                <AnimateFootball height={150} />
              ) : (
                <SubscriptionForm
                  subscriptionState={subscriptionState}
                  isAuthenticated={!!user}
                  pending={pending}
                  error={error}
                  onSubmit={handleSubmit}
                />
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default UpdateStripeSubscriptionPage;
